<template>
  <div class="pe-overlay" @mousedown.self="$emit('cancel')">
    <div class="pe-modal">

      <div class="pe-toolbar">
        <!-- Tools -->
        <button :class="['pe-tool-btn', { active: tool === 'brush' }]" @click="tool = 'brush'" :title="tr.brush">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 3l3 3-12 12-4 1 1-4L18 3z"/></svg>
        </button>
        <button :class="['pe-tool-btn', { active: tool === 'eyedropper' }]" @click="tool = 'eyedropper'" :title="tr.eyedropper">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 22l4-4m10-14l4 4-9 9-5-1 1-5 9-9z"/><circle cx="18" cy="6" r="2"/></svg>
        </button>

        <!-- Color swatch -->
        <div class="pe-color-wrap">
          <div class="pe-color-swatch" :style="{ background: color }"></div>
          <input type="color" v-model="color" class="pe-color-input" :title="tr.color" />
        </div>

        <!-- Brush size (only when brush) -->
        <div v-if="tool === 'brush'" class="pe-size-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="8"/></svg>
          <input type="range" v-model.number="brushSize" min="2" max="80" class="pe-slider" />
          <span class="pe-size-label">{{ brushSize }}px</span>
        </div>

        <!-- Brightness -->
        <div class="pe-size-wrap">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="4"/>
            <line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/>
            <line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/>
            <line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/>
            <line x1="19.78" y1="4.22" x2="17.66" y2="6.34"/><line x1="6.34" y1="17.66" x2="4.22" y2="19.78"/>
          </svg>
          <input type="range" v-model.number="brightness" min="20" max="200" class="pe-slider" @input="applyBrightnessPreview" />
          <span class="pe-size-label">{{ brightness }}%</span>
        </div>

        <div class="pe-spacer"></div>

        <!-- Undo -->
        <button class="pe-tool-btn" @click="undo" :disabled="history.length === 0" :title="tr.undo">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>
        </button>

        <!-- Save -->
        <button class="pe-save-btn" @click="save">{{ tr.save }}</button>

        <!-- Cancel -->
        <button class="pe-cancel-btn" @click="$emit('cancel')">✕</button>
      </div>

      <!-- Canvas -->
      <div class="pe-canvas-wrap">
        <canvas
          ref="canvas"
          class="pe-canvas"
          :style="{ cursor: cursorStyle }"
          @mousedown="onPointerDown"
          @mousemove="onPointerMove"
          @mouseup="onPointerUp"
          @mouseleave="onPointerUp"
          @touchstart.prevent="onTouchStart"
          @touchmove.prevent="onTouchMove"
          @touchend.prevent="onPointerUp"
        ></canvas>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PhotoEditor',
  props: {
    src:  { type: String, required: true },
    lang: { type: String, default: 'fr' },
  },
  emits: ['save', 'cancel'],

  data() {
    return {
      tool: 'brush',
      color: '#ffffff',
      brushSize: 20,
      brightness: 100,
      drawing: false,
      history: [],   // array of ImageData snapshots for undo
      tainted: false,
    };
  },

  computed: {
    tr() {
      const map = {
        fr: { brush: 'Pinceau', eyedropper: 'Pipette', color: 'Couleur', undo: 'Annuler', save: '✓ Sauver', corsError: 'Impossible de sauvegarder : image protégée (CORS). Télécharge d\'abord l\'image localement puis re-uploade-la.' },
        en: { brush: 'Brush',   eyedropper: 'Eyedropper', color: 'Color', undo: 'Undo', save: '✓ Save', corsError: 'Cannot save: protected image (CORS). Download the image locally first, then re-upload it.' },
        he: { brush: 'מברשת',  eyedropper: 'טפטפת',    color: 'צבע',  undo: 'בטל', save: '✓ שמור', corsError: 'לא ניתן לשמור: תמונה מוגנת (CORS). הורד את התמונה מקומית ואז העלה אותה מחדש.' },
      };
      return map[this.lang] || map.fr;
    },
    cursorStyle() {
      return this.tool === 'eyedropper' ? 'cell' : 'crosshair';
    },
  },

  mounted() {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // needed for Firebase Storage URLs
    img.onload = () => {
      const canvas = this.$refs.canvas;
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
    };
    img.onerror = () => {
      // crossOrigin failed (CORS) — reload without it (canvas will be tainted, undo disabled)
      const img2 = new Image();
      img2.onload = () => {
        const canvas = this.$refs.canvas;
        canvas.width  = img2.naturalWidth;
        canvas.height = img2.naturalHeight;
        canvas.getContext('2d').drawImage(img2, 0, 0);
        this.tainted = true;
      };
      img2.src = this.src;
    };
    img.src = this.src;
  },

  methods: {
    /* ── coordinate helpers ── */
    getPos(clientX, clientY) {
      const rect  = this.$refs.canvas.getBoundingClientRect();
      const scale = this.$refs.canvas.width / rect.width;
      return {
        x: (clientX - rect.left) * scale,
        y: (clientY - rect.top)  * scale,
      };
    },

    /* ── pointer events ── */
    onPointerDown(e) {
      const { x, y } = this.getPos(e.clientX, e.clientY);
      if (this.tool === 'eyedropper') {
        this.pickColor(x, y);
        this.tool = 'brush'; // switch back to brush after picking
        return;
      }
      // Save snapshot for undo (disabled if canvas is tainted by cross-origin image)
      if (!this.tainted) {
        try {
          const ctx = this.$refs.canvas.getContext('2d');
          this.history.push(ctx.getImageData(0, 0, this.$refs.canvas.width, this.$refs.canvas.height));
          if (this.history.length > 20) this.history.shift();
        } catch (e) {
          this.tainted = true;
        }
      }
      this.drawing = true;
      this.drawDot(x, y);
    },

    onPointerMove(e) {
      if (!this.drawing || this.tool !== 'brush') return;
      const { x, y } = this.getPos(e.clientX, e.clientY);
      this.drawDot(x, y);
    },

    onPointerUp() {
      this.drawing = false;
    },

    onTouchStart(e) {
      const t = e.touches[0];
      this.onPointerDown({ clientX: t.clientX, clientY: t.clientY });
    },

    onTouchMove(e) {
      const t = e.touches[0];
      this.onPointerMove({ clientX: t.clientX, clientY: t.clientY });
    },

    /* ── drawing ── */
    drawDot(x, y) {
      const ctx = this.$refs.canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(x, y, this.brushSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.fill();
    },

    /* ── eyedropper ── */
    pickColor(x, y) {
      if (this.tainted) return;
      try {
        const ctx  = this.$refs.canvas.getContext('2d');
        const [r, g, b] = ctx.getImageData(Math.round(x), Math.round(y), 1, 1).data;
        this.color = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
      } catch (e) { this.tainted = true; }
    },

    /* ── undo ── */
    undo() {
      if (!this.history.length) return;
      const snap = this.history.pop();
      this.$refs.canvas.getContext('2d').putImageData(snap, 0, 0);
    },

    /* ── brightness preview ── */
    applyBrightnessPreview() {
      this.$refs.canvas.style.filter = `brightness(${this.brightness}%)`;
    },

    /* ── save ── */
    save() {
      try {
        const src = this.$refs.canvas;
        // Bake the brightness filter into the exported image
        const out = document.createElement('canvas');
        out.width  = src.width;
        out.height = src.height;
        const ctx = out.getContext('2d');
        ctx.filter = `brightness(${this.brightness}%)`;
        ctx.drawImage(src, 0, 0);
        const dataUrl = out.toDataURL('image/jpeg', 0.92);
        this.$emit('save', dataUrl);
      } catch (e) {
        alert(this.tr.corsError);
      }
    },
  },
};
</script>

<style scoped>
.pe-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.pe-modal {
  background: #1e1e2e;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  max-height: 95vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6);
}

/* ── toolbar ── */
.pe-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #2a2a3e;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  flex-wrap: wrap;
}

.pe-tool-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.05);
  color: #ccc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
}
.pe-tool-btn:hover  { background: rgba(255,255,255,0.12); }
.pe-tool-btn.active { background: #3b82f6; border-color: #3b82f6; color: #fff; }
.pe-tool-btn:disabled { opacity: 0.3; cursor: default; }

.pe-color-wrap {
  position: relative;
  width: 38px;
  height: 38px;
}
.pe-color-swatch {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 2px solid rgba(255,255,255,0.3);
  cursor: pointer;
}
.pe-color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  padding: 0;
  border: none;
}

.pe-size-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #aaa;
}
.pe-slider { width: 80px; accent-color: #3b82f6; }
.pe-size-label { font-size: 12px; color: #aaa; min-width: 32px; }

.pe-spacer { flex: 1; }

.pe-save-btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: #22c55e;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
}
.pe-save-btn:hover { background: #16a34a; }

.pe-cancel-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.05);
  color: #aaa;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pe-cancel-btn:hover { background: rgba(239,68,68,0.3); color: #fff; }

/* ── canvas ── */
.pe-canvas-wrap {
  overflow: auto;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
  padding: 8px;
}

.pe-canvas {
  max-width: 100%;
  max-height: calc(95vh - 70px);
  display: block;
  touch-action: none;
}
</style>
